import { Injectable } from '@angular/core';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { NgxsActionHelper } from 'app/projects/core/src/lib/services/action.helper';
import { Observable, race } from 'rxjs';
import {
    CreatePerformancePeriod,
    CreatePerformancePeriodFailure,
    CreatePerformancePeriodSuccess,
    DeletePerformancePeriod,
    DeletePerformancePeriodFailure,
    DeletePerformancePeriodSuccess,
    FetchPerformancePeriods,
    FetchPerformancePeriodsFailure,
    FetchPerformancePeriodsSuccess,
    UpdatePerformancePeriod,
    UpdatePerformancePeriodFailure,
    UpdatePerformancePeriodSuccess,
} from '../actions/index';
import { PerformancePeriod } from '../models/performance-period.model';
import { PerformancePeriodState } from '../models/performance-period.state';
import { PerformancePeriodsState } from '../models/performance-periods.state';
import { map, take } from 'rxjs/operators';
import { ICreatePerformancePeriodActionPayload } from '../interfaces/create-performance-period.action.payload.interface';

@Injectable()
export class PerformancePeriodService {
    @Select(PerformancePeriodState.getEntities)
    performancePeriods$: Observable<PerformancePeriod[]>;

    @Select(PerformancePeriodsState.getFilteredEntities)
    filteredPerformancePeriods$: Observable<PerformancePeriod[]>;

    constructor(private _store: Store, private _action$: Actions, private _actionHelper: NgxsActionHelper) {}

    getPerformancePeriods(): PerformancePeriod[] {
        return this._store.selectSnapshot(PerformancePeriodsState.getFilteredEntities);
    }

    async createPerformancePeriod(performancePeriod: ICreatePerformancePeriodActionPayload): Promise<boolean> {
        this._store.dispatch(new CreatePerformancePeriod(performancePeriod));

        return await this._actionHelper.race(CreatePerformancePeriodSuccess, CreatePerformancePeriodFailure).toPromise();
    }

    async fetchPerformancePeriods(pageSize?: number, pageNumber?: number, sortBy?: string): Promise<boolean> {
        this._store.dispatch(
            new FetchPerformancePeriods({
                pageSize,
                pageNumber,
                sortBy,
            })
        );

        return await this._actionHelper.race(FetchPerformancePeriodsSuccess, FetchPerformancePeriodsFailure).toPromise();
    }

    async deletePerformancePeriod(id: string): Promise<boolean> {
        this._store.dispatch(new DeletePerformancePeriod(id));

        return await this._actionHelper.race(DeletePerformancePeriodSuccess, DeletePerformancePeriodFailure).toPromise();
    }

    async updatePerformancePeriod(performancePeriod: PerformancePeriod): Promise<PerformancePeriod> {
        this._store.dispatch(new UpdatePerformancePeriod(performancePeriod));

        return race(
            this._action$.pipe(
                ofActionDispatched(UpdatePerformancePeriodSuccess),
                map((action: UpdatePerformancePeriodSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(UpdatePerformancePeriodFailure),
                map((action: UpdatePerformancePeriodFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }
}
